import { defineComponent as _defineComponent } from 'vue'
import { computed, inject } from "vue";
import BaseEditableCard from "@/components-UI/BaseEditableCard.vue";
import BaseList from "@/components-UI/list/BaseList.vue";
import BaseListItem from "@/components-UI/list/BaseListItem.vue";
import AffiliateMainInfoItem from "./AffiliateMainInfoItem.vue";
import { patchAffiliateSettings } from "@/api/user";
import { translateLang, translateTheme } from "@/utils/translate";
import { Affiliate } from "@/services/affiliates/Affiliate";
import { AFFILIATE_EDIT_KEY, AFFILIATE_INFO_EDIT_TYPE, AFFILIATE_INFO_TYPE, EDITABLE_FIELD } from "./types";
import { useCardEdit } from "./useCardEdit";
import { useTimezonesStore } from "@/store_v3/TimezonesStore";
import { storeToRefs } from "pinia";

interface Props {
    affiliate: Affiliate;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'CabinetSettingsCard',
  props: {
    affiliate: null
  },
  emits: ["saved"],
  setup(__props: any, { emit }: { emit: ({ (e: 'saved'): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
    affiliate: Affiliate;
}



const ctx = inject(AFFILIATE_EDIT_KEY)!;

const OWNED_KEYS: EDITABLE_FIELD[] = [
    EDITABLE_FIELD.LANGUAGE,
    EDITABLE_FIELD.THEME,
    EDITABLE_FIELD.OPEN_ERID,
    EDITABLE_FIELD.ADVANCE_PAYMENTS,
    EDITABLE_FIELD.TIMEZONE,
];

const langItems = [
    { id: 'en', text: 'Английский' },
    { id: 'ru', text: 'Русский' },
];

const themeItems = [
    { id: 'light', text: 'Светлая' },
    { id: 'dark', text: 'Тёмная' },
];

const { items: timezoneItems } = storeToRefs(useTimezonesStore());
useTimezonesStore().getTimezoneItems();

const getOffsetLabel = (zone: string): string => {
    try {
        const label = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
            .formatToParts(new Date())
            .find(p => p.type === 'timeZoneName')?.value ?? '';
        return label.replace('GMT', 'UTC').replace(/^UTC$/, 'UTC+0');
    } catch {
        return '';
    }
};

const getTimezoneValue = (zone: string | null) => {
    if (!zone) return '';
    const found = timezoneItems.value.find(i => i.id === zone);
    if (found) return found.text;
    const offset = getOffsetLabel(zone);
    return offset ? `${zone} (${offset})` : zone;
};

const isEditableAdvancePayments = computed(() => {
    const isMaster = props.affiliate.master_id === null || props.affiliate.master_id === props.affiliate.id;
    const isBalance = !!props.affiliate.balance?.enabled!;
    return isMaster && !isBalance;
});

const updateSettings = async (key: EDITABLE_FIELD, value: any) => {
    await patchAffiliateSettings(props.affiliate.user_id!, { key, value });
};

const updateTheme = async () => {
    await updateSettings(EDITABLE_FIELD.THEME, ctx.editableFields.value[EDITABLE_FIELD.THEME]);
};

const updateOpenErid = async () => {
    await updateSettings(EDITABLE_FIELD.OPEN_ERID, ctx.editableFields.value[EDITABLE_FIELD.OPEN_ERID]);
};

const updateAdvancePayments = async () => {
    await updateSettings(EDITABLE_FIELD.ADVANCE_PAYMENTS, ctx.editableFields.value[EDITABLE_FIELD.ADVANCE_PAYMENTS]);
};

const updateTimezone = async () => {
    await updateSettings(EDITABLE_FIELD.TIMEZONE, ctx.editableFields.value[EDITABLE_FIELD.TIMEZONE]);
};

// === Card edit ===

const { isEdit, isLoading, isDisableEdit, editInfo, resetInfo } = useCardEdit({
    affiliateId: () => props.affiliate.id!,
    getItems: () => items.value,
    editableFields: () => ctx.editableFields.value,
    getChangedValues: () => ctx.getChangedValues.value,
    resetFields: ctx.resetFields,
    ownedKeys: OWNED_KEYS,
    onAfterSave: () => emit('saved'),
});

// === Items ===

const items = computed(() => ({
    language: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Язык',
        value: ctx.editableFields.value[EDITABLE_FIELD.LANGUAGE],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: langItems,
        textValue: translateLang(props.affiliate.language),
        editMethod: ctx.editItem,
    },
    theme: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Тема',
        value: ctx.editableFields.value[EDITABLE_FIELD.THEME],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: themeItems,
        textValue: translateTheme(props.affiliate.theme),
        editMethod: ctx.editItem,
        updateMethod: updateTheme,
    },
    open_erid: {
        type: AFFILIATE_INFO_TYPE.SWITCH,
        name: 'Маркировка EN',
        value: ctx.editableFields.value[EDITABLE_FIELD.OPEN_ERID],
        isEditable: true,
        editMethod: ctx.editItem,
        updateMethod: updateOpenErid,
    },
    advance_payments: {
        type: AFFILIATE_INFO_TYPE.SWITCH,
        name: 'Досрочки',
        value: ctx.editableFields.value[EDITABLE_FIELD.ADVANCE_PAYMENTS],
        isEditable: true,
        isDisabled: !isEditableAdvancePayments.value,
        editMethod: ctx.editItem,
        updateMethod: updateAdvancePayments,
    },
    timezone: {
        type: AFFILIATE_INFO_TYPE.TEXT,
        name: 'Часовой пояс',
        value: ctx.editableFields.value[EDITABLE_FIELD.TIMEZONE],
        isEditable: true,
        editType: AFFILIATE_INFO_EDIT_TYPE.SELECT,
        selectItems: timezoneItems,
        editMethod: ctx.editItem,
        textValue: getTimezoneValue(props.affiliate.timezone),
        selectData: {
            isClearable: true,
            isAutocomplete: true,
            filter: (item: { text: string, iana: string }, q: string) => {
                const s = q.toLowerCase();
                return item.text.toLowerCase().includes(s) || item.iana.toLowerCase().includes(s);
            },
        },
        updateMethod: updateTimezone,
    },
}));

return { __sfc: true,props, emit, ctx, OWNED_KEYS, langItems, themeItems, timezoneItems, getOffsetLabel, getTimezoneValue, isEditableAdvancePayments, updateSettings, updateTheme, updateOpenErid, updateAdvancePayments, updateTimezone, isEdit, isLoading, isDisableEdit, editInfo, resetInfo, items, BaseEditableCard, BaseList, BaseListItem, AffiliateMainInfoItem }
}

})